import { ColoursObj, LoadingSpinner } from "@eventr/common-storybook";
import { Box, useMediaQuery } from "@mui/material";
import { useGlobalRouter } from "feat/globalRouter/useGlobalRouter";
import { ItineraryHeaderPane } from "feat/home/itinerary-header-pane";
import { lazy, Suspense, useEffect, useRef, useState, type ComponentType, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useBoundStore } from "state/bound.store";
import { useEphemeralStore } from "state/useEphemeralStore";
import { useHomeTabStore } from "state/itineraryState/homeTab.store";
import { useBackgroundImageUrl } from "state/windowState/window.store";
import {
  LEADING_PANE_MIN_WIDTH,
  PHONE_MAX_WIDTH,
  TRAILING_PANE_MAX_WIDTH,
  resolveCurrentScreenLayout,
  SHEET_WIDTH,
  TAB_RAIL_WIDTH,
  type PaneGroupId,
  type ScreenLayoutResolution,
} from "../screen-layout";
import { Pane } from "./pane";
import { TrailingDefault } from "./pane-trailing-default";
import { leadingRouteOf, sheetBackgroundOf } from "./pane-navigation";
import { canSplit, leadingWidthFor, readPaneSplit, writePaneSplit } from "./pane-split";
import { PaneSplitter } from "./pane-splitter";
import { useTabRailLeft } from "../tab-rail-side.store";

/** The widest window treated as a device screen, which splits at its middle. */
const DEVICE_MAX_WIDTH = 1023;

const named = <K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K) =>
  lazy(() => load().then((module) => ({ default: module[name] })));

const LEADING: Record<PaneGroupId, ComponentType> = {
  home: named(() => import("app/home/components/home-leading-pane"), "HomeLeadingPane"),
  chat: named(() => import("app/chat/chat"), "ChatPage"),
  team: named(() => import("app/team/team.routes"), "TeamPageWithFilter"),
  account: named(() => import("app/account/account"), "AccountPage"),
  edit: named(() => import("app/edit/edit"), "EditPage"),
};

// Headed by the itinerary header, as they are in one column. Home's leading pane draws its own.
const ITINERARY_HEADED: readonly PaneGroupId[] = ["chat", "team"];

/** Whether the trailing pane shows the route rather than content the shell draws itself. */
const trailingShowsRoute = ({ trailingPane }: ScreenLayoutResolution) =>
  trailingPane === "route" || trailingPane === "summary" || trailingPane === "map";

/** A screen as a card, centred in whatever holds it, optionally over a cover photo. */
function SheetCard({ backgroundImageUrl, children }: { backgroundImageUrl?: string; children: ReactNode }) {
  return (
    <Box
      sx={{
        width: SHEET_WIDTH,
        height: "min(900px, 90%)",
        borderRadius: "16px",
        overflow: "hidden",
        bgcolor: ColoursObj.backgrounds.primary,
        ...(backgroundImageUrl && { backgroundImage: `url(${backgroundImageUrl})`, backgroundSize: "cover" }),
        boxShadow: "0px 8px 24px rgba(16, 55, 92, 0.25)",
      }}
    >
      <Pane side="sheet">{children}</Pane>
    </Box>
  );
}

function Sheet({ children }: { children: ReactNode }) {
  const { goBack } = useGlobalRouter();
  return (
    <Box
      onClick={(event) => event.target === event.currentTarget && goBack()}
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 1200,
        display: "grid",
        placeItems: "center",
        bgcolor: "rgba(16, 55, 92, 0.4)",
      }}
    >
      <SheetCard>{children}</SheetCard>
    </Box>
  );
}

interface PaneShellProps {
  layout: ScreenLayoutResolution;
  /** Whether the panes may show; the route renders alone while false. */
  enabled: boolean;
  /** Height of the area the panes fill, in px. */
  height: number;
  /** The app's routes matched against another location. */
  routesAt: (pathname: string) => ReactNode;
  /** The matched route. */
  children: ReactNode;
}

/**
 * The two-pane layout: the leading tab's list beside the trailing pane, which holds the matched
 * route, or the tab's own content with any sheet route drawn over both. A card route, such as
 * welcome or sign-in, is drawn alone in a sheet's card. Outside the two-pane layout it renders the route alone.
 */
export function PaneShell({ layout, enabled, height, routesAt, children }: PaneShellProps) {
  const coverImage = useBoundStore((s) => s.getCurrentEventImageUrl());
  const tabRailLeft = useTabRailLeft();
  const { pathname, state } = useLocation();
  // The welcome landing photo, which the frame behind the card would otherwise paint unseen.
  const backgroundImageUrl = useBackgroundImageUrl();
  const active = enabled && layout.layout === "two-pane";
  const mapIsFullscreen = useEphemeralStore((s) => s.mapIsFullscreen);

  // Where the user dragged the bar between the panes, as the leading pane's share of their width.
  const gridRef = useRef<HTMLDivElement>(null);
  const [split, setSplit] = useState(readPaneSplit);
  const [draggedWidth, setDraggedWidth] = useState<number | null>(null);
  const [gridWidth, setGridWidth] = useState(0);
  // Across a fold the panes stay split at it.
  const onFold = useMediaQuery("(horizontal-viewport-segments: 2)");
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const observer = new ResizeObserver(() => setGridWidth(grid.clientWidth));
    observer.observe(grid);
    return () => observer.disconnect();
  }, [active]);
  const leadingWidth = onFold ? null : (draggedWidth ?? leadingWidthFor(split, gridWidth));

  // Under a sheet, the panes show the route it was opened over, when known.
  const background = layout.sheet ? sheetBackgroundOf(state) : undefined;
  const panes = background ? resolveCurrentScreenLayout(background) : layout;
  const { leadingPane, trailingPane } = panes;

  // Home's trailing pane is the home tab it opens on in one column.
  useEffect(() => {
    if (!active || leadingPane !== "home" || (trailingPane !== "summary" && trailingPane !== "map")) return;
    const { homeTab, setHomeTab } = useHomeTabStore.getState();
    if (homeTab !== trailingPane) setHomeTab(trailingPane);
  }, [active, leadingPane, trailingPane]);

  if (layout.card) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", height, bgcolor: ColoursObj.backgrounds.secondary }}>
        <SheetCard backgroundImageUrl={pathname === "/" ? backgroundImageUrl : undefined}>{children}</SheetCard>
      </Box>
    );
  }
  if (!active || !leadingPane) return <>{children}</>;

  const Leading = LEADING[leadingPane];
  const route = background ? routesAt(background) : layout.sheet ? undefined : children;
  // A list opened from the tab leads while one of its entries trails.
  const leadingRoute = panes.trailingPane === "route" ? leadingRouteOf(background ? undefined : state) : undefined;
  const leading = panes.leadingShowsRoute ? (
    route
  ) : leadingRoute ? (
    routesAt(leadingRoute)
  ) : ITINERARY_HEADED.includes(leadingPane) ? (
    <ItineraryHeaderPane>
      <Leading />
    </ItineraryHeaderPane>
  ) : (
    <Leading />
  );
  const trailingRoute = panes.leadingShowsRoute ? undefined : route;
  // A fullscreen map takes the leading pane's space too.
  const mapFills = mapIsFullscreen && !layout.sheet && leadingPane === "home" && trailingPane === "map";

  return (
    <Box sx={{ position: "relative", height }}>
      {leadingPane === "home" && coverImage && (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${coverImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(32px) saturate(1.2)",
            transform: "scale(1.15)",
            opacity: 0.18,
          }}
        />
      )}
      <Box
        ref={gridRef}
        style={
          mapFills
            ? { gridTemplateColumns: "minmax(0, 1fr)", columnGap: 0 }
            : leadingWidth === null
              ? undefined
              : { gridTemplateColumns: `${leadingWidth}px minmax(0, 1fr)` }
        }
        sx={{
          position: "relative",
          display: "grid",
          // Beside the rail the leading pane reaches its widest before the trailing pane grows past a
          // phone; above a bottom bar the panes are halves. On a device-sized window the panes meet
          // at the middle of the screen, where a foldable's fold is.
          gridTemplateColumns:
            layout.tabBar === "rail"
              ? `clamp(${LEADING_PANE_MIN_WIDTH}px, calc(100% - ${PHONE_MAX_WIDTH}px), ${PHONE_MAX_WIDTH}px) minmax(${PHONE_MAX_WIDTH}px, ${TRAILING_PANE_MAX_WIDTH}px)`
              : "repeat(2, minmax(0, 1fr))",
          justifyContent: "center",
          height: "100%",
          ...(layout.tabBar === "rail" && {
            [`@media (max-width: ${DEVICE_MAX_WIDTH}px)`]: {
              gridTemplateColumns: `calc((100% ${tabRailLeft ? "-" : "+"} ${TAB_RAIL_WIDTH}px) / 2) minmax(0, 1fr)`,
            },
          }),
          "@media (horizontal-viewport-segments: 2)": {
            gridTemplateColumns: "env(viewport-segment-right 0 0) 1fr",
            columnGap: "calc(env(viewport-segment-left 1 0) - env(viewport-segment-right 0 0))",
            justifyContent: "stretch",
          },
        }}
      >
        <Pane side="leading" hidden={mapFills}>
          <Suspense fallback={<LoadingSpinner />}>{leading}</Suspense>
        </Pane>
        <Pane side="trailing" solid={trailingPane !== "summary" && trailingPane !== "map"}>
          <Suspense fallback={<LoadingSpinner />}>
            {trailingRoute && trailingShowsRoute(panes) ? (
              trailingRoute
            ) : (
              <TrailingDefault tab={leadingPane} trailing={trailingPane} />
            )}
          </Suspense>
        </Pane>
        {!mapFills && !onFold && canSplit(gridWidth) && (
          <PaneSplitter
            gridRef={gridRef}
            onMove={setDraggedWidth}
            onCommit={(share) => {
              setDraggedWidth(null);
              setSplit(share);
              writePaneSplit(share);
            }}
            onReset={() => {
              setSplit(null);
              writePaneSplit(null);
            }}
          />
        )}
      </Box>
      {layout.sheet && <Sheet>{children}</Sheet>}
    </Box>
  );
}
